import { useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Paperclip, Mail, Phone, FileText } from "lucide-react";

export const CHANNELS = [
  { value: "written_email", label: "Written / Email", icon: Mail },
  { value: "verbal_call", label: "Verbal / Call", icon: Phone },
  { value: "customer_document", label: "Customer document", icon: FileText },
];
export const channelLabel = (c) => CHANNELS.find((x) => x.value === c)?.label || c;

export const ApprovalDialog = ({ project, phase, action, open, onClose, onSaved }) => {
  const fileRef = useRef(null);
  const [form, setForm] = useState({ channel: "written_email", approved_by: "", approved_on: "", notes: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.approved_by.trim()) return toast.error("Who gave the approval?");
    if (form.channel === "verbal_call" && !form.notes.trim()) return toast.error("A short note is required for verbal approvals");
    setSaving(true);
    try {
      let document_id = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("phase_id", phase.phase_id);
        if (action) fd.append("action_id", action.action_id);
        const { data } = await api.post(`/projects/${project.project_id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        document_id = data.document_id;
      }
      await api.post(`/projects/${project.project_id}/approvals`, {
        ...form, approved_on: form.approved_on || null, phase_id: phase.phase_id, action_id: action?.action_id || null, document_id,
      });
      toast.success("Approval recorded");
      setForm({ channel: "written_email", approved_by: "", approved_on: "", notes: "" });
      setFile(null);
      onClose();
      await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="approval-dialog">
        <DialogHeader>
          <DialogTitle>Record approval</DialogTitle>
          <DialogDescription>{action ? action.title : phase.name} — a structured approval counts as valid evidence.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">How was the approval given?</Label>
            <div className="mt-1 grid grid-cols-3 gap-2">
              {CHANNELS.map((c) => (
                <button key={c.value} type="button" onClick={() => setForm({ ...form, channel: c.value })}
                  className={`rounded-md border p-2.5 text-xs flex flex-col items-center gap-1 transition ${form.channel === c.value ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:bg-slate-50"}`}
                  data-testid={`approval-channel-${c.value}`}>
                  <c.icon className="h-4 w-4" />{c.label}
                </button>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Approved by</Label><Input placeholder="e.g. Anna Müller (Customer)" value={form.approved_by} onChange={(e) => setForm({ ...form, approved_by: e.target.value })} data-testid="approval-by-input" /></div>
            <div><Label className="text-xs">Approval date (default today)</Label><Input type="date" value={form.approved_on} onChange={(e) => setForm({ ...form, approved_on: e.target.value })} data-testid="approval-date-input" /></div>
          </div>
          <div>
            <Label className="text-xs">Note {form.channel === "verbal_call" ? <span className="text-red-600">(required for verbal)</span> : "(optional)"}</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder={form.channel === "verbal_call" ? "Who you spoke to, what was agreed…" : "Optional context"} data-testid="approval-notes-input" />
          </div>
          <div>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="approval-file-input" />
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="approval-attach-btn"><Paperclip className="h-4 w-4 mr-1" />{file ? file.name : "Attach file (optional)"}</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="approval-save-btn">{saving ? "Saving…" : "Record approval"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
